import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { title: "", body: "", image_url: "", author_name: "", author_avatar: "" };

export function PostDialog({ open, onOpenChange, communityId, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.body.trim()) {
      toast.error("Post text is required");
      return;
    }
    setBusy(true);
    try {
      const body = { body: form.body.trim() };
      if (form.title.trim()) body.title = form.title.trim();
      if (form.image_url.trim()) body.image_url = form.image_url.trim();
      if (form.author_name.trim()) body.author_name = form.author_name.trim();
      if (form.author_avatar.trim()) body.author_avatar = form.author_avatar.trim();
      await api.createPost(communityId, body);
      toast.success("Post published");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="post-dialog">
        <DialogHeader>
          <DialogTitle>New post</DialogTitle>
          <DialogDescription>Publish to the official verified feed. Leave author fields blank to post as “Amoreska”.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="p-title">Title <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="p-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Where to next? ✈️" data-testid="post-title" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-body">Post text</Label>
            <Textarea id="p-body" rows={4} value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="Drop the ONE country you'd cross the world for…" data-testid="post-body" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-image">Image URL <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="p-image" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…jpg" data-testid="post-image" />
          </div>
          {form.image_url && (
            <div className="h-36 rounded-lg overflow-hidden bg-secondary ring-1 ring-border/60">
              <img src={form.image_url} alt="preview" className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="p-author">Author name <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="p-author" value={form.author_name} onChange={(e) => set("author_name", e.target.value)} placeholder="Amoreska" data-testid="post-author-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-avatar">Author avatar URL <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="p-avatar" value={form.author_avatar} onChange={(e) => set("author_avatar", e.target.value)} placeholder="https://…jpg" data-testid="post-author-avatar" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="post-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish post"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_MIXER = { title: "", description: "", starts_at: "", cover_url: "", location: "" };

export function MixerDialog({ open, onOpenChange, communityId, onDone }) {
  const [form, setForm] = useState(EMPTY_MIXER);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY_MIXER);
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.starts_at) {
      toast.error("Start date & time is required");
      return;
    }
    const iso = new Date(form.starts_at).toISOString(); // local -> UTC ISO-8601 (Z)
    setBusy(true);
    try {
      const body = { title: form.title.trim(), starts_at: iso };
      if (form.description.trim()) body.description = form.description.trim();
      if (form.cover_url.trim()) body.cover_url = form.cover_url.trim();
      if (form.location.trim()) body.location = form.location.trim();
      await api.createMixer(communityId, body);
      toast.success("Mixer scheduled");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="mixer-dialog">
        <DialogHeader>
          <DialogTitle>Schedule a mixer</DialogTitle>
          <DialogDescription>Plan a live group hangout members can RSVP to.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="m-title">Title</Label>
            <Input id="m-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Friday Amapiano Night" data-testid="mixer-title" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-desc">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea id="m-desc" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Log on and vibe…" data-testid="mixer-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="m-start">Starts at</Label>
              <Input id="m-start" type="datetime-local" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} data-testid="mixer-starts-at" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="m-loc">Location <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="m-loc" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Live in-app" data-testid="mixer-location" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-cover">Cover URL <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="m-cover" value={form.cover_url} onChange={(e) => set("cover_url", e.target.value)} placeholder="https://…jpg" data-testid="mixer-cover" />
          </div>
          {form.starts_at && (
            <p className="text-xs text-muted-foreground" data-testid="mixer-utc-hint">
              Sends as UTC: {new Date(form.starts_at).toISOString()}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="mixer-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Schedule mixer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
